import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Button} from '@wandb/weave/components/Button';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {cloneDeep} from 'lodash';
import React, {useCallback, useContext, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';
import {twMerge} from 'tailwind-merge';

import {ViewSource} from '../../generated/graphql';
import {useApolloClient} from '../../state/hooks';
import {saveView} from '../../state/reports/hooks';
import {usePanelComments} from '../../state/reports/usePanelComments';
import {RunQueryContext} from '../../state/runs/context';
import * as RunHooks from '../../state/runs/hooks';
import {useViewer} from '../../state/viewer/hooks';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {useRunSets} from '../../state/views/runSet/hooks';
import {DateFormat, format} from '../../util/date';
import {
  EMPTY_PANEL_BANK_CONFIG,
  EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT,
} from '../../util/panelbankConfigs';
import {isMobile} from '../../util/panelbankFlow';
import {getFullWidthPanelLayout} from '../../util/panelbankGrid';
import {ImageExportType} from '../../util/panelExport';
import {getPanelSpec} from '../../util/panelTypes';
import {useRampFlagMetricChangeNotifications} from '../../util/rampFeatureFlags';
import * as Report from '../../util/report';
import {truncateString} from '../../util/runhelpers';
import {useFeatureFlagVegaEnabled} from '../../util/useFeatureFlag';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {useDefinedMetricContext} from '../PanelBank/DefinedMetricContext';
import {PanelImageExportModal} from '../PanelImageExportModal';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import {useSemanticLegendSettingsContext} from '../WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsContextProvider';
import {RunColorOptions, Settings} from '../WorkspaceDrawer/Settings/types';
import {
  useWorkspaceDrawerContextMaybe,
  WorkspaceDrawerContextType,
} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useDetectPanelVariant, useGoToBaseUrl} from './hooks';
import {ExportType, PanelActionsMenu} from './PanelActionsMenu';
import {usePanelContext} from './PanelContextProvider';
import {PanelDragHandle} from './PanelDragHandle';
import {PanelExportAPI} from './PanelExportAPI';
import {PanelExportCSV} from './PanelExportCSV';
import {PanelFullScreenButton} from './PanelFullScreenButton';
import {PanelRunMetricButton} from './PanelRunMetricButton';
import {PanelSettingsButton} from './PanelSettingsButton';
import {ShareReportTriggerWrapper} from './ShareReportTriggerWrapper';
import {PanelPopupMode} from './types';

// report names are limited to 128 chars. When editing report titles we prevent
// users from exceeding this. But here, we dont have a limit on the title length
// and title length is used to create the report name. So we need to truncate
// also actual limit is 128, but truncateString uses single char elipses which is
// 3 chars in backend
const MAX_BACKEND_TITLE_LENGTH = 128 - 2;

export const PanelHeader = ({
  defaultMoveToSectionRef,
  shouldTurnOnComments,
}: {
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  shouldTurnOnComments: boolean;
}) => {
  const {customRunColorsRef, customRunNamesRef, runSetRefs} =
    usePanelsSharedRefsContext();

  const {
    isReadOnly,
    panelBankSectionConfigRef,
    panelRef,
    panel,
    inheritedSettings,
    setPanelPopupMode,
    trackPanelControls,
  } = usePanelContext();

  const {isFullScreenPanelPage} = useDetectPanelVariant();

  const goToBaseURL = useGoToBaseUrl();

  const panelType = panel.viewType;
  const panelSpec = getPanelSpec(panelType);

  let panelTitle: string | undefined;
  if (panelSpec && panelSpec.getTitleFromConfig && panel && panel.config) {
    panelTitle = panelSpec.getTitleFromConfig(panel.config as any);
  }

  const {panelCommentCount, openPanelComments, addComment, isHighlighted} =
    usePanelComments({panelRef});

  const runSets = useRunSets(runSetRefs);
  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const {semanticLegendSettings, isMetricBasedColors} =
    useSemanticLegendSettingsContext();

  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);
  const {mergeFilters, runSetName} = useContext(RunQueryContext);
  const {entityName, projectName} = RunHooks.useRunSetsQuery({runSetRefs});

  const viewer = useViewer();
  const client = useApolloClient();

  const vegaPanelEnabled = useFeatureFlagVegaEnabled(entityName);

  const [reportID, setReportID] = useState<undefined | string>();
  const [reportName, setReportName] = useState('');

  const [exportType, setExportType] = useState<ExportType>();
  const [imageExportType, setImageExportType] = useState<ImageExportType>();
  const unsetExportType = useCallback(() => setExportType(undefined), []);

  const {definedGlobs: defineMetricGlobDefinitions} = useDefinedMetricContext();

  const viewerIsMemberOfTeam = viewer?.teams.edges.some(
    edge => edge.node.name === entityName
  );

  const sharePanel = useCallback(async () => {
    const report = Report.fromSection(
      {
        runSets,
        customRunColors: customRunColors,
        customRunNames,
        settings: inheritedSettings?.linePlot as Settings,
        panelBankConfig: cloneDeep(EMPTY_PANEL_BANK_CONFIG),
        panelBankSectionConfig: {
          ...cloneDeep(EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT),
          panels: [{...panel, layout: getFullWidthPanelLayout()}],
        },
        semanticLegendSettings: isMetricBasedColors
          ? {
              ...semanticLegendSettings,
              runColorOptions: RunColorOptions.MetricBasedColors,
            }
          : undefined,
      },
      {mergeFilters, runSetName, noSectionHeader: true},
      undefined,
      defineMetricGlobDefinitions
    );

    const dateFormat = DateFormat.YEAR_MONTH_DAY_HOUR_MINUTE_SECOND;
    const extraCharsLength = 3 + dateFormat.length; // 3 for ' ()'
    const maybeTruncatedPanelTitle =
      panelTitle != null
        ? truncateString(
            panelTitle,
            MAX_BACKEND_TITLE_LENGTH - extraCharsLength,
            TruncationType.Middle
          )
        : panelTitle;
    const name = `${maybeTruncatedPanelTitle} (${format(
      new Date(),
      dateFormat
    )})`;
    const savedView = await saveView({
      client,
      entityName,
      projectName,
      reportName: name,
      draft: false,
      config: report,
      createdUsing: ViewSource.WandbUiSharePanel,
    });
    if (savedView.id == null) {
      toast('Error occured while trying to share panel');
    } else {
      setReportID(savedView.id);
      setReportName(name);
    }

    trackPanelControls({
      action: 'Share panel clicked',
      sharedPanelReportId: savedView.id,
    });
  }, [
    client,
    customRunColors,
    customRunNames,
    defineMetricGlobDefinitions,
    entityName,
    mergeFilters,
    panel,
    inheritedSettings,
    panelTitle,
    projectName,
    runSetName,
    runSets,
    trackPanelControls,
    isMetricBasedColors,
    semanticLegendSettings,
  ]);

  const [isOverflowMenuActive, setIsOverflowMenuActive] = useState(false);
  const workspaceDrawerContext = useWorkspaceDrawerContextMaybe();
  let openWorkspaceDrawer:
    | undefined
    | WorkspaceDrawerContextType['openWorkspaceDrawer'] = undefined;

  if (workspaceDrawerContext) {
    openWorkspaceDrawer = workspaceDrawerContext.openWorkspaceDrawer;
  }
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = selectedAccount?.name ?? '';

  const {data: runMetricNotificationEnabled} =
    useRampFlagMetricChangeNotifications(orgName, entityName);

  return (
    <TailwindContents>
      <div
        className={twMerge(
          'flex shrink-0',
          // since we have more real estable in full screen mode, we should add more spacing between panel header and panel content itself
          isFullScreenPanelPage ? 'mb-18 min-h-[32px]' : 'mb-4 min-h-[24px]',
          // on a regular workspace OR report where multiple panels are visible, we want to only show
          // the actionable left side items when the mouse is over the panel. it should always be visible in full screen where we only
          // show 1 panel
          !isFullScreenPanelPage && 'opacity-0 hover:opacity-100',
          // since we hide the actionable items until mouse hover on a regular workspace or report where we display multiple panels,
          // we still want to show the actionable items when overflow menu is active
          isOverflowMenuActive && 'opacity-100'
        )}>
        {!isReadOnly && !isFullScreenPanelPage && (
          <PanelDragHandle panelRef={panelRef} />
        )}
        <div
          className={twMerge(
            // when in full screen mode where only 1 panel is visible, we want to position the back button to the left
            isFullScreenPanelPage && 'flex w-full justify-between',
            // on a regular workspace OR report where multiple panels are visible, the actions should be rendered on top
            // of the drag handle and there's no back button to consider
            !isFullScreenPanelPage && 'absolute right-8'
          )}>
          {isFullScreenPanelPage && (
            <Button
              data-test="full-screen-back"
              icon="back"
              variant="ghost"
              onClick={goToBaseURL}
            />
          )}

          <div className={twMerge('flex')}>
            {panelType === 'Run History Line Plot' &&
              runMetricNotificationEnabled &&
              openWorkspaceDrawer != null && (
                <PanelRunMetricButton
                  panelBankSectionConfigRef={panelBankSectionConfigRef}
                  panelRef={panelRef}
                />
              )}
            {/* View spec */}
            {isReadOnly && panelType === 'Vega2' && (
              <Popup
                content="View panel spec"
                size="mini"
                position="top center"
                trigger={
                  <LegacyWBIcon
                    name="show"
                    aria-label="View panel spec"
                    className="panel-action"
                    onClick={() => setPanelPopupMode(PanelPopupMode.VIEW_SPEC)}
                  />
                }
              />
            )}
            {/* View full screen */}
            {!isMobile() && !isFullScreenPanelPage && <PanelFullScreenButton />}
            {/* Edit panel */}
            {!isReadOnly &&
              !isMobile() &&
              !panelSpec.noEditMode &&
              (panelType !== 'Vega' || vegaPanelEnabled) && (
                <PanelSettingsButton />
              )}
            {isReadOnly && shouldTurnOnComments && (
              <Button
                icon="forum-chat-bubble"
                tooltip={'Add a comment'}
                tooltipProps={{className: 'py-8 px-12'}}
                variant="ghost"
                size="small"
                name="chat"
                title="Add comment"
                aria-label="Add comment"
                className="mr-8"
                onClick={addComment}
              />
            )}
            {/* Three-dot popup menu */}
            {!isReadOnly && (
              <>
                {reportID && (
                  <ShareReportTriggerWrapper
                    reportID={reportID}
                    reportName={reportName}
                    unsetReportID={() => setReportID(undefined)}
                    userIsMemberOfTeam={viewerIsMemberOfTeam}
                  />
                )}
                {panelBankSectionConfigRef != null && (
                  <PanelActionsMenu
                    panelSpec={panelSpec}
                    panelBankSectionConfigRef={panelBankSectionConfigRef}
                    setExportType={setExportType}
                    setImageExportType={setImageExportType}
                    defaultMoveToSectionRef={defaultMoveToSectionRef}
                    isOpen={isOverflowMenuActive}
                    setIsOpen={setIsOverflowMenuActive}
                    sharePanel={sharePanel}
                  />
                )}
                <PanelExportAPI
                  open={exportType === ExportType.API}
                  onClose={unsetExportType}
                />
                <PanelExportCSV
                  open={exportType === ExportType.CSV}
                  onClose={unsetExportType}
                />
                {imageExportType && (
                  <PanelImageExportModal
                    open={exportType === ExportType.Image}
                    type={imageExportType}
                    onClose={unsetExportType}
                    panelSpec={panelSpec}
                  />
                )}
              </>
            )}
            {shouldTurnOnComments &&
              panelCommentCount != null &&
              panelCommentCount > 0 && (
                <Popup
                  content="View comments"
                  inverted
                  size="mini"
                  position="top center"
                  trigger={
                    <div className="panel-comments" onClick={openPanelComments}>
                      <span>{panelCommentCount}</span>
                    </div>
                  }
                />
              )}
          </div>
        </div>
      </div>
    </TailwindContents>
  );
};
